import {
  cloneElement, forwardRef, useCallback, useEffect, useId, useRef, useState,
  type FocusEvent, type KeyboardEvent, type ReactElement, type ReactNode,
} from 'react'
import { createPortal, flushSync } from 'react-dom'
import { Popover as BasePopover } from '@base-ui/react/popover'
import { cn } from '@/lib/utils'
import './popover.css'

type PopoverName =
  /** A visible heading names the popover. */
  | { title: Exclude<ReactNode, null | undefined | boolean>; label?: string }
  /** Without a heading, `label` names it for assistive technology. */
  | { title?: undefined; label: string }

type PopoverRootProps = Pick<BasePopover.Root.Props, 'open' | 'defaultOpen' | 'onOpenChange' | 'onOpenChangeComplete'>

/** An element, or a ref to one, to place the popover against. */
export type PopoverAnchor = BasePopover.Positioner.Props['anchor']

type PopoverOpener =
  /** The usual case: the trigger opens and closes it, and keeps `aria-expanded` and focus return in step. */
  | {
      /** The focusable, named element that opens it, for example an IconButton. */
      trigger: ReactElement
      /** Places it against this element instead of the trigger. */
      anchor?: PopoverAnchor
    }
  /**
   * An opener that runs its own open logic. The popover is controlled and placed against `anchor`, which
   * it does not manage: the opener sets its own `aria-expanded` and `aria-controls`, moves focus in itself,
   * and a press on it or focus moving to it reaches `onOpenChange` as an outside press or focus-out.
   */
  | { trigger?: undefined; anchor: PopoverAnchor; open: boolean }

export type PopoverProps = PopoverName & PopoverRootProps & PopoverOpener & Omit<BasePopover.Popup.Props, 'title' | 'children' | 'className' | 'render'> & {
  /** The content. Keep it short: an explanation, a detail or one follow-up action. */
  children?: ReactNode
  description?: ReactNode
  /** Actions, right-aligned. */
  footer?: ReactNode
  side?: 'top' | 'bottom' | 'left' | 'right'
  align?: 'start' | 'center' | 'end'
  /**
   * What it does when it doesn't fit (Base UI's): by default it flips to the other side, then to the other axis. A
   * dropdown list keeps its side and scrolls instead: `{ side: 'none', align: 'shift', fallbackAxisSide: 'none' }`.
   */
  collisionAvoidance?: BasePopover.Positioner.Props['collisionAvoidance']
  /** Also open on pointer hover. Press, tap and the keyboard always open it. */
  openOnHover?: boolean
  /**
   * Also show it while the trigger has keyboard focus from Tab, like a tooltip, without moving focus in. The trigger
   * is described by the content. Enter or Space opens it for real and moves focus in; Tab moves on and hides it.
   * Needs a `trigger`.
   */
  openOnFocus?: boolean
  /** Portal target, for example an active fullscreen root. Defaults to the document body. */
  container?: HTMLElement | null
  /** Classes for the popover surface. */
  className?: string
}

// Focus from Tab or Shift+Tab, as opposed to a press or a programmatic move such as a dialog's initial focus or
// focus returning after Escape. Only that focus shows an `openOnFocus` popover, so it never takes the first Escape
// meant for a parent dialog.
let tabbedAt = Number.NEGATIVE_INFINITY
let trackingTab = false
const TAB_FOCUS_WINDOW = 1000
const MODIFIER_KEYS = new Set(['Shift', 'Control', 'Alt', 'Meta'])

function trackTabNavigation() {
  if (trackingTab || typeof document === 'undefined') return
  trackingTab = true
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Tab') tabbedAt = performance.now()
    else if (!MODIFIER_KEYS.has(event.key)) tabbedAt = Number.NEGATIVE_INFINITY
  }, true)
  document.addEventListener('pointerdown', () => { tabbedAt = Number.NEGATIVE_INFINITY }, true)
}

const focusCameFromTab = () => performance.now() - tabbedAt < TAB_FOCUS_WINDOW

const TABBABLE = 'a[href], button:not(:disabled), input:not(:disabled):not([type="hidden"]), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])'

/** Detail anchored to its trigger. It stays open until dismissed by Escape, a press outside or the trigger. */
export const Popover = forwardRef<HTMLDivElement, PopoverProps>(function Popover(
  {
    open, defaultOpen, onOpenChange, onOpenChangeComplete, trigger, anchor, title, label, description, footer, children,
    side = 'bottom', align = 'center', collisionAvoidance, openOnHover = false, openOnFocus = false, initialFocus, finalFocus, container, className,
    ...popup
  },
  ref,
) {
  const [handle] = useState(() => BasePopover.createHandle())
  const actionsRef = useRef<BasePopover.Root.Actions>(null)
  const popupRef = useRef<HTMLDivElement | null>(null)
  const baseId = useId()
  const triggerProps = (trigger?.props ?? {}) as { id?: string; 'aria-describedby'?: string }
  const triggerId = triggerProps.id ?? `${baseId}-trigger`
  const descriptionId = `${baseId}-description`
  const bodyId = `${baseId}-body`
  const hasBody = children != null && children !== false

  const focusPeek = openOnFocus && Boolean(trigger)
  const [shown, setShown] = useState(defaultOpen ?? false)
  const isOpen = open ?? shown
  // Shown by Tab focus and not yet opened for real: focus stays on the trigger and isn't returned on close.
  const [peeking, setPeeking] = useState(false)
  const peekingRef = useRef(false)
  const openingFromFocus = useRef(false)
  const setPeek = (value: boolean) => {
    peekingRef.current = value
    setPeeking(value)
  }

  useEffect(() => {
    if (focusPeek) trackTabNavigation()
  }, [focusPeek])

  const setPopupRef = useCallback((node: HTMLDivElement | null) => {
    popupRef.current = node
    if (typeof ref === 'function') ref(node)
    else if (ref) ref.current = node
  }, [ref])

  /** Moves focus in the way Base UI does on a keyboard open: the chosen element, else the first tabbable or the popup. */
  const focusInside = () => {
    const popupElement = popupRef.current
    if (!popupElement) return
    const chosen = resolveInitialFocus(initialFocus, 'keyboard', popupElement)
    if (chosen === false || chosen === undefined) return
    const element = chosen instanceof HTMLElement ? chosen : popupElement.querySelector<HTMLElement>(TABBABLE) ?? popupElement
    element.focus()
  }

  const handleOpenChange: NonNullable<PopoverRootProps['onOpenChange']> = (next, details) => {
    if (peekingRef.current && details.reason === 'trigger-press') {
      // Pressing the trigger while it only shows on focus opens it for real: it stays open and focus moves in.
      // Base UI either asks to close (a repeated press) or to stay open; both end the same way.
      if (next) onOpenChange?.(next, details)
      else details.cancel()
      setPeek(false)
      focusInside()
      return
    }
    onOpenChange?.(next, details)
    if (details.isCanceled) {
      if (next && openingFromFocus.current) setPeek(false)
      return
    }
    if (open === undefined) setShown(next)
    if (next && !openingFromFocus.current) setPeek(false)
  }

  const showOnFocus = (event: FocusEvent<HTMLElement>) => {
    if (event.currentTarget !== event.target || handle.isOpen || !focusCameFromTab()) return
    setPeek(true)
    openingFromFocus.current = true
    try {
      handle.open(triggerId)
    } finally {
      openingFromFocus.current = false
    }
  }

  const moveOnWithTab = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key !== 'Tab' || !peekingRef.current || !handle.isOpen) return
    // Like a tooltip, Tab moves past the trigger, not into the content. Unmount at once (no exit transition) so the
    // popover's focus guards are gone before the browser picks the next element.
    flushSync(() => handle.close())
    if (!handle.isOpen) flushSync(() => actionsRef.current?.unmount())
  }

  const describedBy = focusPeek && isOpen
    ? [triggerProps['aria-describedby'], description ? descriptionId : undefined, hasBody ? bodyId : undefined].filter(Boolean).join(' ') || undefined
    : undefined
  const triggerElement = trigger && describedBy ? cloneElement(trigger, { 'aria-describedby': describedBy }) : trigger

  const popover = (
    <BasePopover.Root
      open={open}
      defaultOpen={defaultOpen}
      onOpenChange={handleOpenChange}
      onOpenChangeComplete={onOpenChangeComplete}
      actionsRef={actionsRef}
      handle={focusPeek ? handle : undefined}
    >
      {triggerElement && (
        <BasePopover.Trigger
          id={focusPeek ? triggerId : undefined}
          handle={focusPeek ? handle : undefined}
          render={triggerElement}
          openOnHover={openOnHover}
          onFocus={focusPeek ? showOnFocus : undefined}
          onKeyDown={focusPeek ? moveOnWithTab : undefined}
        />
      )}
      <BasePopover.Portal container={container ?? undefined}>
        <BasePopover.Positioner
          anchor={anchor}
          side={side}
          align={align}
          sideOffset={8}
          collisionPadding={12}
          collisionAvoidance={collisionAvoidance}
          className="play-popover__positioner"
        >
          <BasePopover.Popup
            ref={setPopupRef}
            aria-label={title ? undefined : label}
            initialFocus={focusPeek ? (type) => (peekingRef.current ? false : resolveInitialFocus(initialFocus, type, popupRef.current)) : initialFocus}
            finalFocus={peeking ? false : finalFocus}
            data-peek={peeking ? '' : undefined}
            className={cn('play-theme play-popover', className)}
            {...popup}
          >
            {title && <BasePopover.Title className="play-popover__title">{title}</BasePopover.Title>}
            {description && <BasePopover.Description id={descriptionId} className="play-popover__description">{description}</BasePopover.Description>}
            {hasBody && <div id={bodyId} className="play-popover__body">{children}</div>}
            {footer && <div className="play-popover__footer">{footer}</div>}
          </BasePopover.Popup>
        </BasePopover.Positioner>
      </BasePopover.Portal>
    </BasePopover.Root>
  )
  if (trigger) return popover
  // Base UI keeps a popover's tab position where it renders, normally right after its trigger. Without a trigger
  // there is nothing to follow, so it sits at the end of its container, like any portal: Tab from the anchor
  // moves on, and the anchor moves focus in itself.
  return typeof document === 'undefined' ? null : createPortal(popover, container ?? document.body)
})

type InitialFocus = BasePopover.Popup.Props['initialFocus']
type InteractionType = Parameters<Extract<NonNullable<InitialFocus>, (...args: never[]) => unknown>>[0]

/** Base UI's own default, or the consumer's choice, for a popover opened for real. */
function resolveInitialFocus(initialFocus: InitialFocus, type: InteractionType, popup: HTMLDivElement | null) {
  if (initialFocus === undefined) return type === 'touch' ? popup : true
  if (typeof initialFocus === 'function') return initialFocus(type)
  if (typeof initialFocus === 'object' && initialFocus !== null) return initialFocus.current
  return initialFocus
}
